import useSWR from "swr";
import { endpointsIssues } from "lib/actions/endpoints/endpointsIssues";
import { fetcher } from "lib/actions/fetcher";
import { mapIssueFromResponse } from "components/modules/Issues/lib/actions/mapIssueFromResponse";
import { IssueTypeModel } from "components/modules/Issues/lib/types/IssueTypeModel";
import { IssueUserModel } from "components/modules/Issues/lib/types/IssueUserModel";
import { IssueQueryResultsResponseModel } from "components/modules/Issues/lib/types/IssueQueryResultsResponseModel";
import { useRouteParamString } from "lib/hooks/useRouteParam";
import { useCurrentProjectId } from "lib/hooks/useProject";
import { issuePatch } from "components/modules/Issues/lib/actions/issuePatch";
import { issueCreate } from "components/modules/Issues/lib/actions/issueCreate";
import { IssueCreateRequestModel } from "components/modules/Issues/lib/types/IssueCreateRequestModel";
import { issueDelete } from "components/modules/Issues/lib/actions/issueDelete";
import { Operation } from "fast-json-patch";

export function useProjectIssues() {
  const projectId = useCurrentProjectId();
  const {
    data: issuesData,
    error: issuesError,
    mutate: mutateIssues,
  } = useSWR<IssueQueryResultsResponseModel>(
    projectId && endpointsIssues.projectIssues(projectId),
    fetcher,
  );

  const { data: typesData, error: typesError } = useSWR<IssueTypeModel[]>(
    projectId && endpointsIssues.projectIssueTypes(projectId),
    fetcher,
  );
  const { data: usersData, error: usersError } = useSWR<IssueUserModel[]>(
    projectId && endpointsIssues.projectIssueUsers(projectId),
    fetcher,
  );

  const issues = issuesData?.results.map((x, i) => {
    const issue = mapIssueFromResponse(x);
    return issue;
  });

  function getType(id: string) {
    return typesData?.find((t) => t.id === id);
  }

  function getUser(id: string) {
    return usersData?.find((u) => u.id === id);
  }

  async function createIssue(model: IssueCreateRequestModel) {
    const creatingTask = issueCreate(projectId, model);
    await mutateIssues(async (issuesData) => {
      const issue = await creatingTask;
      if (issuesData) {
        return {
          ...issuesData,
          results: [
            ...issuesData.results,
            {
              ...issue,
              attachments: null,
              geoanchor: issue.geoanchor ?? null,
            },
          ],
          limit: issuesData.limit,
          offset: issuesData.offset,
          total: issuesData.total + 1,
        };
      } else {
        return {
          results: [
            {
              ...issue,
              attachments: null,
              geoanchor: issue.geoanchor ?? null,
            },
          ],
          limit: Number.MAX_VALUE,
          offset: 0,
          total: 1,
        };
      }
    }, false);
    return await creatingTask;
  }

  async function patchIssue(id: string, patch: Operation[]) {
    const updateTask = issuePatch(projectId, id, patch);
    await mutateIssues(
      async (current) => {
        const updatedIssue = await updateTask;
        if (!current?.results) return current;
        return {
          ...current,
          results: current?.results.map((issue) =>
            issue.id === id ? { ...issue, ...updatedIssue } : issue,
          ),
        };
      },
      {
        revalidate: false,
        optimisticData: issuesData,
      },
    );
    return await updateTask;
  }

  async function deleteIssue(issueId: string) {
    await issueDelete(projectId, issueId);
    await mutateIssues(
      async () => {
        return {
          ...issuesData,
          results: issuesData?.results.filter((issue) => issue.id !== issueId),
        };
      },
      {
        revalidate: false,
        optimisticData: issuesData,
      },
    );
  }

  return {
    issues: issues ?? [],
    types: typesData ?? [],
    users: usersData ?? [],
    isLoading:
      (!issuesData && !issuesError) ||
      (!typesData && !typesError) ||
      (!usersData && !usersError),
    hasError: issuesError || typesError || usersError,
    getType,
    getUser,
    createIssue,
    mutateIssues: async () => await mutateIssues(),
    patchIssue,
    deleteIssue,
  };
}

export function useIssue(issueId: string) {
  const { issues, isLoading, hasError } = useProjectIssues();

  const issue = issues?.find((v) => v.id === issueId);

  return {
    issue,
    isLoading,
    hasError,
  };
}

export function useCurrentIssueId(): string | null {
  const [issueId] = useRouteParamString("issueId");
  return issueId;
}

export function useCurrentIssue() {
  const issueId = useCurrentIssueId();
  return {
    issueId: issueId,
    isOneOpen: issueId != null,
    ...useIssue(issueId),
  };
}
